import React, { createContext, useContext, useState, useEffect } from 'react';
import { startOfWeek, endOfWeek, addDays, subDays, format, addMonths, subMonths, startOfMonth, endOfMonth, isSameMonth, isSameDay } from 'date-fns';
import { talentScheduleService } from '../api/apiService';

const TalentCalendarContext = createContext();

export const useTalentCalendar = () => useContext(TalentCalendarContext);

export const TalentCalendarProvider = ({ children, talentId }) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('week');
  const [events, setEvents] = useState([]);
  const [availabilities, setAvailabilities] = useState([]);
  const [vacations, setVacations] = useState([]);
  const [awaitings, setAwaitings] = useState([]);
  const [secondoptions, setSecondoptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter options
  const [userOptions, setUserOptions] = useState([]);
  const [clientOptions, setClientOptions] = useState([]);
  const [setOptions, setSetOptions] = useState([]);
  const [locationOptions, setLocationOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);

  // Filters state
  const [filters, setFilters] = useState({
    userEmail: null,
    bookingStatus: null,
    client: null,
    set: null,
    location: null,
    searchTerm: null
  });

  // Navigation functions
  const goToPreviousWeek = () => {
    setCurrentDate(prev => subDays(prev, 7));
  };

  const goToNextWeek = () => {
    setCurrentDate(prev => addDays(prev, 7));
  };

  const goToPreviousMonth = () => {
    setCurrentDate(prev => subMonths(prev, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(prev => addMonths(prev, 1));
  };

  const goToPreviousDay = () => {
    setCurrentDate(prev => subDays(prev, 1));
  };

  const goToNextDay = () => {
    setCurrentDate(prev => addDays(prev, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date());
  };

  // Get the current week's date range
  const getCurrentWeekRange = () => {
    const start = startOfWeek(currentDate, { weekStartsOn: 1 });
    const end = endOfWeek(currentDate, { weekStartsOn: 1 });
    return {
      start: format(start, 'yyyy-MM-dd'),
      end: format(end, 'yyyy-MM-dd')
    };
  };

  // Get days for month view
  const getDaysForMonthView = () => {
    const monthStart = startOfMonth(currentDate);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    
    const days = [];
    let day = startDate;
    
    while (day <= endDate) {
      days.push({
        date: day,
        isCurrentMonth: isSameMonth(day, monthStart),
        isToday: isSameDay(day, new Date()),
        isSelected: isSameDay(day, selectedDate),
      });
      day = addDays(day, 1);
    }
    
    return days;
  };

  // Get days for week view - 7 days (Monday to Sunday)
  const getDaysForWeekView = () => {
    const weekStart = startOfWeek(currentDate, { weekStartsOn: 1 });
    
    const days = [];
    let day = weekStart;
    
    // Generate 7 days (Monday to Sunday)
    for (let i = 0; i < 7; i++) {
      days.push({
        date: day,
        isToday: isSameDay(day, new Date()),
        isSelected: isSameDay(day, selectedDate),
      });
      day = addDays(day, 1);
    }
    
    return days;
  };

  // Get hours for day view
  const getHoursForDayView = () => {
    const hours = [];
    for (let i = 0; i < 24; i++) {
      hours.push({
        hour: i,
        label: format(new Date().setHours(i, 0, 0, 0), 'h:mm a'),
      });
    }
    return hours;
  };

  // Get events for a specific date
  const getEventsForDate = (date) => {
    return events.filter(event => {
      const eventDate = new Date(event.start);
      return isSameDay(eventDate, date);
    });
  };

  // Set event filters
  const setEventFilters = (newFilters) => {
    setFilters(prevFilters => ({
      ...prevFilters,
      ...newFilters
    }));
  };

  // Fetch talent schedule data
  const fetchTalentSchedule = async () => {
    // console.log('fetchTalentSchedule called with talentId:', talentId);
    
    if (!talentId) {
      // console.log('No talentId provided, skipping fetch');
      return;
    }

    setLoading(true);
    try {
      let startDate, endDate;
      
      if (view === 'month') {
        startDate = startOfMonth(currentDate);
        endDate = endOfMonth(currentDate);
      } else if (view === 'week') {
        startDate = startOfWeek(currentDate);
        endDate = endOfWeek(currentDate);
      } else {
        startDate = currentDate;
        endDate = currentDate;
      }

      // console.log('Fetching schedule for dates:', {
        // start: format(startDate, 'yyyy-MM-dd'),
        // end: format(endDate, 'yyyy-MM-dd'),
        // talentId: talentId
      // });

      // Get all dates between start and end
      const dates = [];
      let current = new Date(startDate);
      while (current <= endDate) {
        dates.push(format(current, 'yyyy-MM-dd'));
        current = addDays(current, 1);
      }

      // console.log('Fetching schedule for dates:', dates);

      const responses = await Promise.all(
        dates.map(date => talentScheduleService.getTalentScheduleForDate(talentId, date))
      );

      // console.log('All API Responses:', responses);

      // Process the response and set events
      const processedEvents = [];
      const allAvailabilities = [];
      const allVacations = [];
      const allAwaitings = [];
      const allSeconOptions = [];
      
      // Process each day's response
      responses.forEach(response => {
        // console.log('Processing response:', response);
        
        if (response && response.data && response.data.schedule) {
          const schedule = response.data.schedule;
          
          // Process bookings (existing logic)
          if (schedule.bookings && Array.isArray(schedule.bookings)) {
            const dayEvents = schedule.bookings.map(booking => {
              const event = {
                id: booking.id || booking.firebase_id,
                title: `${booking.client?.label || 'Unknown Client'} - ${booking.talent_name || 'Unknown Talent'}`,
                start_date: booking.start_date,
                end_date: booking.end_date,
                status: booking.status,
                position: booking.position?.label || '',
                client: booking.client?.label || '',
                talent_name: booking.talent_name || '',
                group_color: '#15a326'
              };
              // console.log('Created booking event:', event);
              return event;
            });
            
            processedEvents.push(...dayEvents);
          }
          
          // Process availabilities
          if (schedule.availabilities && Array.isArray(schedule.availabilities)) {
            const availabilityEntries = schedule.availabilities.map(item => ({
              startDate: new Date(item.start_date),
              endDate: new Date(item.end_date),
              addedAt: new Date(item.added_at || item.created_at || new Date())
            }));
            allAvailabilities.push(...availabilityEntries);
          }
          
          // Process vacations
          if (schedule.vacations && Array.isArray(schedule.vacations)) {
            const vacationEntries = schedule.vacations.map(item => ({
              startDate: new Date(item.start_date),
              endDate: new Date(item.end_date),
              addedAt: new Date(item.added_at || item.created_at || new Date())
            }));
            allVacations.push(...vacationEntries);
          }
          
          // Process awaitings
          if (schedule.awaitings && Array.isArray(schedule.awaitings)) {
            const awaitingEntries = schedule.awaitings.map(item => ({
              startDate: new Date(item.start_date),
              endDate: new Date(item.end_date),
              addedAt: new Date(item.added_at || item.created_at || new Date())
            }));
            allAwaitings.push(...awaitingEntries);
          }
          
          // Process secondoptions
          if (schedule.secondoptions && Array.isArray(schedule.secondoptions)) {
            const secondoptionEntries = schedule.secondoptions.map(item => ({
              startDate: new Date(item.start_date),
              endDate: new Date(item.end_date),
              addedAt: new Date(item.added_at || item.created_at || new Date())
            }));
            allSeconOptions.push(...secondoptionEntries);
          }
        }
      });

      // console.log('Final processed events array:', processedEvents);
      // console.log('Processed availability data:', allAvailabilities.length);
      // console.log('Processed vacation data:', allVacations.length);
      // console.log('Processed awaiting data:', allAwaitings.length);
      // console.log('Processed secondoption data:', allSeconOptions.length);
      
      // Store the availability/vacation/awaiting data in context for use by TalentCalendarApi
      setAvailabilities(allAvailabilities);
      setVacations(allVacations);
      setAwaitings(allAwaitings);
      setSecondoptions(allSeconOptions);
      
      // Apply client-side filters including search
      let filteredEvents = processedEvents;
      
      if (filters.userEmail) {
        filteredEvents = filteredEvents.filter(event => 
          event.proposedBy === filters.userEmail
        );
      }
      
      if (filters.client) {
        filteredEvents = filteredEvents.filter(event => 
          event.client === filters.client
        );
      }
      
      if (filters.set) {
        filteredEvents = filteredEvents.filter(event => 
          event.set === filters.set
        );
      }
      
      if (filters.location) {
        filteredEvents = filteredEvents.filter(event => {
          // Extract location string from the event's location field
          let eventLocationString = '';
          if (event.location && typeof event.location === 'object') {
            eventLocationString = event.location.short || event.location.name || event.location.city || '';
          } else if (typeof event.location === 'string') {
            eventLocationString = event.location;
          }
          
          return eventLocationString === filters.location;
        });
      }
      
      if (filters.bookingStatus) {
        filteredEvents = filteredEvents.filter(event => 
          event.status === filters.bookingStatus
        );
      }
      
      // Apply search filter if searchTerm exists
      if (filters.searchTerm && filters.searchTerm.trim() !== '') {
        const searchTerm = filters.searchTerm.toLowerCase().trim();
        filteredEvents = filteredEvents.filter(event => {
          // Search across multiple fields
          const searchableFields = [
            event.talent_name,      // Talent name
            event.client,           // Client name
            event.proposedBy,       // Proposed by (user)
            event.position,         // Position/function
            event.title,            // Event title
            event.status,           // Status
            event.set,              // Set type
            // Location (handle object or string)
            typeof event.location === 'object' 
              ? (event.location?.short || event.location?.name || event.location?.city || '')
              : (event.location || ''),
            // Any additional info
            event.additionalInfo,
            event.SonstigeInfo
          ];
          
          // Check if any field contains the search term
          return searchableFields.some(field => {
            if (field && typeof field === 'string') {
              return field.toLowerCase().includes(searchTerm);
            }
            return false;
          });
        });
      }
      
      setEvents(filteredEvents);

      // Update filter options based on the events
      const uniqueUsers = [...new Set(processedEvents.map(event => event.proposedBy))];
      const uniqueClients = [...new Set(processedEvents.map(event => event.client))];
      const uniqueSets = [...new Set(processedEvents.map(event => event.set))];
      const uniqueLocations = [...new Set(processedEvents.map(event => {
        // Extract location.short for filters
        if (event.location && typeof event.location === 'object') {
          return event.location.short || event.location.name || '';
        } else if (typeof event.location === 'string') {
          return event.location;
        }
        return '';
      }).filter(location => location !== ''))];
      const uniqueStatuses = [...new Set(processedEvents.map(event => event.status))];

      setUserOptions(uniqueUsers.map(user => ({ value: user, label: user })));
      setClientOptions(uniqueClients.map(client => ({ value: client, label: client })));
      setSetOptions(uniqueSets.map(set => ({ value: set, label: set })));
      setLocationOptions(uniqueLocations.map(location => ({ value: location, label: location })));
      setStatusOptions(uniqueStatuses.map(status => ({ value: status, label: status })));

    } catch (err) {
      console.error('Error fetching talent schedule:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // Fetch data when currentDate, view, or filters change
  useEffect(() => {
    fetchTalentSchedule();
  }, [currentDate, view, filters, talentId]);

  const value = {
    currentDate,
    selectedDate,
    view,
    events,
    availabilities,
    vacations,
    awaitings,
    secondoptions,
    loading,
    error,
    filters,
    userOptions,
    clientOptions,
    setOptions,
    locationOptions,
    statusOptions,
    setCurrentDate,
    setSelectedDate,
    setView,
    setEvents,
    setAvailabilities,
    setVacations,
    setAwaitings,
    setSecondoptions,
    setLoading,
    setError,
    setEventFilters,
    goToPreviousWeek,
    goToNextWeek,
    goToPreviousMonth,
    goToNextMonth,
    goToPreviousDay,
    goToNextDay,
    goToToday,
    getCurrentWeekRange,
    getDaysForMonthView,
    getDaysForWeekView,
    getHoursForDayView,
    getEventsForDate
  };

  return (
    <TalentCalendarContext.Provider value={value}>
      {children}
    </TalentCalendarContext.Provider>
  );
}; 